/**
 * RpcStatusNav Component
 * 
 * Global RPC health status widget for the top navigation.
 * Shows real-time latency, WebSocket status, and opens a detailed popover with metrics.
 * Includes RPC Endpoint selection directly in the dropdown.
 */

import { useState, useMemo, useCallback, useEffect } from 'react';
import { Activity, RefreshCw, Server, Zap, Clock, CheckCircle2, AlertTriangle, XCircle, ChevronDown, ChevronRight, Wifi, WifiOff, ArrowRightLeft, Hash, Loader2 } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Label } from '@/components/ui/label';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { useRpcTelemetry, RpcStatus, WsConnectionStatus } from '@/hooks/useRpcTelemetry';
import { useRPCProvider } from '@/hooks/useRPCProvider';
import { ProviderId } from '@/services/rpc/types';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';
import { debug } from "@/lib/debug";

// Provider options for selection (Custom removed for simplicity)
interface ProviderOption {
  id: 'auto' | ProviderId;
  label: string;
}

const PROVIDER_OPTIONS: ProviderOption[] = [
  { id: 'auto', label: 'Auto (recommended)' },
  { id: 'triton', label: 'Triton RPC' },
  { id: 'alchemy', label: 'Alchemy RPC' },
];

// Simple SVG line chart component
function MiniLineChart({ 
  data, 
  width = 200, 
  height = 60,
  color = 'hsl(var(--primary))',
  showArea = false,
}: { 
  data: number[]; 
  width?: number; 
  height?: number;
  color?: string;
  showArea?: boolean;
}) {
  if (data.length < 2) {
    return (
      <div 
        className="flex items-center justify-center text-xs text-muted-foreground"
        style={{ width, height }}
      >
        Collecting data…
      </div>
    );
  }
  
  const padding = 4;
  const chartWidth = width - padding * 2;
  const chartHeight = height - padding * 2;
  
  const min = Math.min(...data);
  const max = Math.max(...data);
  const range = max - min || 1;
  
  const points = data.map((value, index) => {
    const x = padding + (index / (data.length - 1)) * chartWidth;
    const y = padding + chartHeight - ((value - min) / range) * chartHeight;
    return `${x},${y}`;
  }).join(' ');
  
  const areaPath = showArea ? `
    M ${padding},${padding + chartHeight}
    L ${data.map((value, index) => {
      const x = padding + (index / (data.length - 1)) * chartWidth;
      const y = padding + chartHeight - ((value - min) / range) * chartHeight;
      return `${x},${y}`;
    }).join(' L ')}
    L ${padding + chartWidth},${padding + chartHeight}
    Z
  ` : '';
  
  return (
    <svg width={width} height={height} className="overflow-visible">
      {showArea && (
        <path 
          d={areaPath} 
          fill={color}
          fillOpacity={0.15}
        />
      )}
      <polyline
        points={points}
        fill="none"
        stroke={color}
        strokeWidth={1.5}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      {/* Latest point indicator */}
      <circle
        cx={padding + chartWidth}
        cy={padding + chartHeight - ((data[data.length - 1] - min) / range) * chartHeight}
        r={3}
        fill={color}
      />
    </svg>
  );
}

// Get health dot color based on latency thresholds
function getHealthDotStyle(latency: number | null, healthy: boolean): string {
  if (!healthy || latency === null) {
    return 'bg-[#EF4444]'; // Red - unhealthy/error
  }
  if (latency < 300) {
    return 'bg-[#22C55E]'; // Green - healthy
  }
  if (latency <= 500) {
    return 'bg-[#EAB308]'; // Yellow - degraded
  }
  return 'bg-[#EF4444]'; // Red - slow (>500ms)
}

function getStatusConfig(status: RpcStatus) {
  switch (status) {
    case 'healthy':
      return {
        color: 'text-primary',
        bgColor: 'bg-primary/10',
        borderColor: 'border-primary/30',
        icon: CheckCircle2,
        label: 'Healthy',
      };
    case 'slow':
      return {
        color: 'text-yellow-500',
        bgColor: 'bg-yellow-500/10',
        borderColor: 'border-yellow-500/30',
        icon: AlertTriangle,
        label: 'Slow',
      };
    case 'error':
      return {
        color: 'text-destructive',
        bgColor: 'bg-destructive/10',
        borderColor: 'border-destructive/30',
        icon: XCircle,
        label: 'Error',
      };
  }
}

function getWsStatusConfig(status: WsConnectionStatus) {
  switch (status) {
    case 'connected':
      return { color: 'text-primary', icon: Wifi, label: 'Connected' };
    case 'reconnecting':
      return { color: 'text-yellow-500', icon: Wifi, label: 'Reconnecting' };
    case 'disconnected':
      return { color: 'text-muted-foreground', icon: WifiOff, label: 'Disconnected' };
  }
}

export function RpcStatusNav() {
  const [isOpen, setIsOpen] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);

  const {
    activeProvider,
    userPreference,
    setUserPreference,
    providers,
    getProviderStatus,
    httpEndpoint,
    wsEndpoint,
    isFailingOver,
  } = useRPCProvider();
  
  // Optimistic UI: track pending selection for instant feedback
  const [optimisticSelection, setOptimisticSelection] = useState<'auto' | ProviderId | null>(null);
  
  // Cache last known WS status and latency so they persist when dropdown is closed
  const [cachedWsStatus, setCachedWsStatus] = useState<WsConnectionStatus>('connected');
  const [cachedLatency, setCachedLatency] = useState<number | null>(null);
  
  // Pass active provider endpoints to telemetry so metrics match the selection
  // Only pass endpoints when popover is open to avoid unnecessary WebSocket connections
  const telemetry = useRpcTelemetry({ 
    httpEndpoint, 
    wsEndpoint,
    enabled: isOpen, // Lazy telemetry - only active when popover is open
  });
  
  // Delay transition from cached to live WS status to allow connection time
  const [wsTransitionReady, setWsTransitionReady] = useState(false);
  
  useEffect(() => {
    if (isOpen) {
      // Wait a bit before switching to live status to avoid "reconnecting" flash
      setWsTransitionReady(false);
      const timer = setTimeout(() => setWsTransitionReady(true), 400);
      return () => clearTimeout(timer);
    } else {
      setWsTransitionReady(false);
    }
  }, [isOpen]);
  
  // Update cached values when telemetry is active
  useEffect(() => {
    if (isOpen) {
      if (telemetry.wsStatus !== 'disconnected') {
        setCachedWsStatus(telemetry.wsStatus);
      }
      if (telemetry.latencyStats?.p50 != null) {
        setCachedLatency(telemetry.latencyStats.p50);
      }
    }
  }, [isOpen, telemetry.wsStatus, telemetry.latencyStats?.p50]);
  
  // Sync optimistic selection with actual preference when it catches up
  useEffect(() => {
    if (optimisticSelection && userPreference === optimisticSelection) {
      setOptimisticSelection(null);
    }
  }, [userPreference, optimisticSelection]);

  // Background latency polling - always runs regardless of dropdown state
  // Updates every 5 seconds to keep latency indicator fresh
  useEffect(() => {
    if (!httpEndpoint) return;

    let isMounted = true;

    const measureLatency = async () => {
      if (!isMounted) return;
      try {
        const start = performance.now();
        const response = await fetch(httpEndpoint, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            jsonrpc: '2.0',
            id: 'ping',
            method: 'getHealth',
          }),
        });
        if (response.ok && isMounted) {
          const latency = Math.round(performance.now() - start);
          setCachedLatency(latency);
        }
      } catch {
        // Silently ignore - don't update cached latency on error
      }
    };

    // Initial measurement
    measureLatency();

    // Poll every 5 seconds for real-time updates
    const interval = setInterval(measureLatency, 5000);

    return () => {
      isMounted = false;
      clearInterval(interval);
    };
  }, [httpEndpoint]);
  
  // Display selection: show optimistic if pending, otherwise actual
  const displaySelection = optimisticSelection ?? userPreference;
  const isSwitching = optimisticSelection !== null;
  
  // Get active provider name
  const activeProviderStatus = activeProvider ? getProviderStatus(activeProvider) : null;
  const activeProviderName = activeProviderStatus?.name || 'Unknown';
  
  // Check if a provider is available (has been configured)
  const isProviderAvailable = useCallback((providerId: ProviderId): boolean => {
    if (!providers) return false;
    // Provider exists in the providers map means it was configured
    return providerId in providers;
  }, [providers]);
  
  const statusConfig = getStatusConfig(telemetry.status);
  // Use cached values when dropdown is closed OR during transition - prefer 'connected' until confirmed
  const displayWsStatus = useMemo(() => {
    if (!isOpen) return cachedWsStatus;
    // If telemetry confirms connected, use it immediately
    if (telemetry.wsStatus === 'connected') return 'connected';
    // During transition delay, keep cached status to avoid flash
    if (!wsTransitionReady) return cachedWsStatus;
    // After transition, show actual status
    return telemetry.wsStatus;
  }, [isOpen, wsTransitionReady, telemetry.wsStatus, cachedWsStatus]);
  const displayLatency = isOpen ? telemetry.latencyStats?.p50 : (telemetry.latencyStats?.p50 ?? cachedLatency);
  const wsConfig = getWsStatusConfig(displayWsStatus);
  const StatusIcon = statusConfig.icon;
  const WsIcon = wsConfig.icon;
  
  const latencyData = useMemo(() => 
    telemetry.latencySamples.map(s => s.latency),
    [telemetry.latencySamples]
  );
  
  const tpsData = useMemo(() => 
    telemetry.tpsSamples.map(s => s.tps),
    [telemetry.tpsSamples]
  );
  
  const handleRefresh = async () => {
    setIsRefreshing(true);
    try {
      await telemetry.refresh();
      toast.success('RPC metrics updated');
    } finally {
      setIsRefreshing(false);
    }
  };
  
  const formatTime = (date: Date | null) => {
    if (!date) return 'Never';
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffSec = Math.floor(diffMs / 1000);
    if (diffSec < 60) return `${diffSec}s ago`;
    const diffMin = Math.floor(diffSec / 60);
    return `${diffMin}m ago`;
  };

  // Provider selection handlers - optimistic UI for instant response
  const handleProviderSelection = (value: string) => {
    const providerId = value as 'auto' | ProviderId;
    
    // Optimistic update - UI responds instantly
    setOptimisticSelection(providerId);
    
    if (providerId !== 'auto') {
      const status = getProviderStatus(providerId);
      if (status && !status.healthy) {
        toast.error(`${status.name} is currently experiencing issues. Switching anyway.`);
      }
    }

    // Actual preference update happens async
    setUserPreference(providerId);
    
    if (import.meta.env.DEV) {
      debug.trace('[RpcStatusNav] Provider selected:', providerId);
    }
  };

  const getProviderLatency = (providerId: ProviderId): { latency: number | null; healthy: boolean } => {
    if (!providers) return { latency: null, healthy: false };
    const status = providers[providerId];
    return {
      latency: status?.latency ?? null,
      healthy: status?.healthy ?? false,
    };
  };

  const getActiveProviderInfo = () => {
    if (!activeProvider || !providers) return null;
    const status = providers[activeProvider];
    return status ? { name: status.name, latency: status.latency, healthy: status.healthy } : null;
  };

  const activeInfo = getActiveProviderInfo();
  
  // Determine if connection is healthy for dot color
  const isHealthy = telemetry.status === 'healthy';
  const dotColorClass = getHealthDotStyle(displayLatency ?? null, isHealthy);

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <PopoverTrigger asChild>
              <Button
                variant="ghost"
                size="sm"
                className="h-7 px-2 gap-1.5 font-mono text-xs hover:bg-secondary/50 transition-colors"
              >
                <span className={cn('w-2 h-2 rounded-full', dotColorClass)} />
                <span className="text-muted-foreground">
                  {displayLatency != null ? `${displayLatency}ms` : '---'}
                </span>
              </Button>
            </PopoverTrigger>
          </TooltipTrigger>
          <TooltipContent side="bottom" className="text-xs">
            <div>HTTP: {statusConfig.label}</div>
            <div>WS: {wsConfig.label}</div>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
      
      <PopoverContent
        align="end"
        className="w-[360px] p-0 bg-card border-border"
        sideOffset={8}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-3 py-2 border-b border-border">
          <div className="flex items-center gap-2">
            <Server className="w-4 h-4 text-muted-foreground" />
            <span className="font-medium text-sm">RPC Status</span>
            {isFailingOver && (
              <Badge variant="outline" className="h-5 px-1.5 text-[10px] border-yellow-500/40 bg-yellow-500/10 text-yellow-500 gap-1">
                <ArrowRightLeft className="w-3 h-3" />
                Failover
              </Badge>
            )}
          </div>
          <Button variant="ghost" size="icon" onClick={handleRefresh} disabled={isRefreshing}>
            <RefreshCw className={cn('w-3.5 h-3.5', isRefreshing && 'animate-spin')} />
          </Button>
        </div>

        {/* Compact Status Row */}
        <div className="px-3 py-2 border-b border-border">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              {/* Status Indicator */}
              <div className="flex items-center gap-1.5">
                <StatusIcon className={cn('w-3.5 h-3.5', statusConfig.color)} />
                <span className={cn('text-xs font-medium', statusConfig.color)}>
                  {statusConfig.label}
                </span>
              </div>

              {/* Latency */}
              <div className="flex items-center gap-1 text-xs">
                <Clock className="w-3 h-3 text-muted-foreground" />
                <span className="font-mono text-primary">
                  {telemetry.latency ?? '---'}ms
                </span>
              </div>

              {/* Current Slot */}
              {telemetry.diagnostics.currentSlot && (
                <div className="flex items-center gap-1 text-xs">
                  <Hash className="w-3 h-3 text-muted-foreground" />
                  <span className="font-mono text-muted-foreground">
                    {telemetry.diagnostics.currentSlot.toLocaleString()}
                  </span>
                </div>
              )}

              {/* TPS */}
              <div className="flex items-center gap-1 text-xs">
                <Zap className="w-3 h-3 text-muted-foreground" />
                <span className="font-mono text-primary">
                  {telemetry.tps !== null ? telemetry.tps.toLocaleString() : '---'}
                </span>
              </div>
            </div>

            {/* WebSocket Status */}
            <div className="flex items-center gap-1">
              <WsIcon className={cn('w-3.5 h-3.5', wsConfig.color)} />
            </div>
          </div>

          {/* Active Provider */}
          <div className="flex items-center justify-between mt-2">
            <Badge
              variant="outline"
              className="h-5 px-2 text-[10px] font-medium border-primary/40 bg-primary/10 text-primary gap-1"
            >
              <span className="relative flex h-1.5 w-1.5">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-primary opacity-75" />
                <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-primary" />
              </span>
              {activeProviderName}
            </Badge>
            <span className="text-[10px] text-muted-foreground">
              {formatTime(telemetry.lastUpdated)}
            </span>
          </div>

          {/* Rate Limit Warning */}
          {telemetry.rateLimit.limit !== null && telemetry.rateLimit.remaining !== null && telemetry.rateLimit.remaining < 20 && (
            <div className="flex items-center gap-1 mt-1.5 text-[10px] text-yellow-500">
              <AlertTriangle className="w-3 h-3" />
              <span>Low rate limit: {telemetry.rateLimit.remaining}/{telemetry.rateLimit.limit}</span>
            </div>
          )}
        </div>

        {/* Error details when status is error */}
        {telemetry.status === 'error' && telemetry.diagnostics.lastError && (
          <div className="mx-3 my-2 bg-destructive/10 border border-destructive/30 rounded p-2">
            <p className="text-[10px] text-destructive font-medium">
              Error
              {telemetry.diagnostics.lastErrorStatus && (
                <span className="ml-1 opacity-70">(HTTP {telemetry.diagnostics.lastErrorStatus})</span>
              )}
            </p>
            <p className="text-[10px] text-destructive/80 font-mono break-all mt-0.5">
              {telemetry.diagnostics.lastError}
            </p>
          </div>
        )}

        {/* Advanced Stats - Collapsible */}
        <Collapsible open={showAdvanced} onOpenChange={setShowAdvanced}>
          <CollapsibleTrigger asChild>
            <button className="w-full flex items-center justify-between px-3 py-2 text-xs text-muted-foreground hover:bg-secondary/30 transition-colors border-b border-border">
              <span>Advanced Stats</span>
              <ChevronRight className={cn('w-3.5 h-3.5 transition-transform', showAdvanced && 'rotate-90')} />
            </button>
          </CollapsibleTrigger>
          <CollapsibleContent>
            <div className="p-3 space-y-3 border-b border-border">
              {/* Latency Chart */}
              <div className="space-y-1.5">
                <div className="flex items-center justify-between">
                  <span className="text-[10px] font-medium text-muted-foreground">Latency (ms)</span>
                  <div className="flex gap-2 text-[10px] text-muted-foreground">
                    <span>p50: {telemetry.latencyStats?.p50 ?? '-'}</span>
                    <span>p95: {telemetry.latencyStats?.p95 ?? '-'}</span>
                  </div>
                </div>
                <div className="bg-secondary/30 rounded p-1.5">
                  <MiniLineChart
                    data={latencyData}
                    width={320}
                    height={50}
                    color="hsl(var(--primary))"
                  />
                </div>
              </div>

              {/* TPS Chart */}
              <div className="space-y-1.5">
                <div className="flex items-center justify-between">
                  <span className="text-[10px] font-medium text-muted-foreground">Network TPS</span>
                  <span className="text-[10px] font-mono text-primary">
                    {telemetry.tps !== null ? telemetry.tps.toLocaleString() : '---'}
                  </span>
                </div>
                <div className="bg-secondary/30 rounded p-1.5">
                  <MiniLineChart
                    data={tpsData}
                    width={320}
                    height={50}
                    color="hsl(72, 100%, 50%)"
                    showArea
                  />
                </div>
              </div>

              {/* Diagnostics */}
              <div className="flex gap-2 text-[10px]">
                <div className="flex-1 bg-secondary/30 rounded px-2 py-1">
                  <span className="text-muted-foreground">Version: </span>
                  <span className="font-mono">{telemetry.diagnostics.version?.slice(0, 12) ?? '-'}</span>
                </div>
                <div className="bg-secondary/30 rounded px-2 py-1">
                  <span className={cn(
                    'font-medium',
                    telemetry.diagnostics.health === 'ok' ? 'text-primary' : 'text-muted-foreground'
                  )}>
                    {telemetry.diagnostics.health ?? '-'}
                  </span>
                </div>
              </div>
            </div>
          </CollapsibleContent>
        </Collapsible>

        {/* RPC Endpoint Selection */}
        <div className="p-3">
          <div className="flex items-center justify-between mb-2">
            <p className="text-[10px] font-medium text-muted-foreground">RPC Endpoint</p>
            {isSwitching && (
              <div className="flex items-center gap-1 text-[10px] text-primary">
                <Loader2 className="w-3 h-3 animate-spin" />
                <span>Switching...</span>
              </div>
            )}
          </div>
          <RadioGroup
            value={displaySelection}
            onValueChange={handleProviderSelection}
            className="space-y-1.5"
          >
            {PROVIDER_OPTIONS.map((option) => {
              const isSelected = displaySelection === option.id;
              const isAuto = option.id === 'auto';

              // Check if provider is available (configured)
              const isAvailable = isAuto || isProviderAvailable(option.id as ProviderId);

              let providerLatency: { latency: number | null; healthy: boolean } | null = null;
              if (!isAuto && isAvailable) {
                providerLatency = getProviderLatency(option.id as ProviderId);
              }

              // Health dot color
              const getHealthDotColor = () => {
                if (!providerLatency) return 'bg-muted-foreground';
                if (!providerLatency.healthy) return 'bg-destructive';
                if (providerLatency.latency === null) return 'bg-muted-foreground';
                if (providerLatency.latency < 100) return 'bg-primary';
                if (providerLatency.latency < 500) return 'bg-yellow-500';
                return 'bg-destructive';
              };

              return (
                <Label
                  key={option.id}
                  htmlFor={`rpc-${option.id}`}
                  className={cn(
                    'flex items-center justify-between px-3 py-2 rounded border transition-colors',
                    isAvailable ? 'cursor-pointer' : 'cursor-not-allowed opacity-50',
                    isSelected
                      ? 'border-primary bg-primary/5'
                      : isAvailable
                        ? 'border-border hover:bg-secondary/30'
                        : 'border-border/50'
                  )}
                >
                  <div className="flex items-center gap-2">
                    <RadioGroupItem
                      value={option.id}
                      id={`rpc-${option.id}`}
                      disabled={!isAvailable}
                      className={cn(
                        'h-3.5 w-3.5',
                        isSelected && 'border-primary text-primary'
                      )}
                    />
                    <span className="text-xs font-medium">
                      {isAuto ? 'Auto' : option.label.replace(' RPC', '')}
                    </span>
                    {isAuto && activeInfo && (
                      <span className="text-[10px] text-muted-foreground">
                        ({activeInfo.name})
                      </span>
                    )}
                    {!isAvailable && !isAuto && (
                      <span className="text-[10px] text-muted-foreground">(N/A)</span>
                    )}
                  </div>

                  <div className="flex items-center gap-2">
                    {isAvailable && providerLatency && (
                      <>
                        <span className="text-[10px] font-mono text-muted-foreground">
                          {providerLatency.latency !== null ? `${providerLatency.latency}ms` : '--'}
                        </span>
                        <span className={cn('w-2 h-2 rounded-full', getHealthDotColor())} />
                      </>
                    )}
                  </div>
                </Label>
              );
            })}
          </RadioGroup>
        </div>
      </PopoverContent>
    </Popover>
  );
}
